import React, { useEffect, useMemo, useRef, useState } from "react";

const sanitizeDecimalInput = (raw) => {
  const s = String(raw ?? "");
  let clean = s.replace(/[^0-9.,]/g, "");

  const firstSepIdx = clean.search(/[.,]/);
  if (firstSepIdx !== -1) {
    const before = clean.slice(0, firstSepIdx + 1);
    const after = clean.slice(firstSepIdx + 1).replace(/[.,]/g, "");
    clean = before + after;
  }

  return clean;
};

const MeasuresModal = ({
  open,
  onClose,
  onSave,
  saving,
  error,
  success,
  sex,

  entryDate,
  setEntryDate,

  weightKg,
  weightNotes,
  setWeightKg,
  setWeightNotes,

  form,
  setField,
}) => {
  const firstInputRef = useRef(null);
  const panelRef = useRef(null);
  const [showBody, setShowBody] = useState(false);

  const prevOpenRef = useRef(open);

  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    if (!wasOpen && open) {
      setShowBody(false);
      window.setTimeout(() => {
        if (firstInputRef.current) firstInputRef.current.focus({ preventScroll: true });
      }, 80);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        if (!saving) onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, saving, onClose]);

  const bodySexField = useMemo(() => {
    return sex === "male" ? "chest" : "hip";
  }, [sex]);

  if (!open) return null;

  const safeClose = () => {
    if (!saving) onClose();
  };

  const Field = ({ name, label, value, onChange }) => (
    <label className="block">
      <div className="text-xs font-semibold text-gray-700 mb-1">
        {label}
      </div>
      <input
        value={value ?? ""}
        onChange={(e) => onChange(sanitizeDecimalInput(e.target.value))}
        inputMode="decimal"
        type="text"
        autoComplete="off"
        className="w-full rounded-xl border border-gray-200 px-3 h-10 text-sm font-semibold box-border"
        placeholder="Ex: 0,0"
      />
    </label>
  );

  return (
    <div
      aria-modal="true"
      role="dialog"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-xs transition-opacity duration-300"
      onClick={safeClose}
    >
      <div
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-t-3xl bg-white border-t border-purple-100 shadow-2xl overflow-hidden flex flex-col transition-all duration-300"
        style={{
          maxHeight: "calc(85vh - 40px)",
        }}
      >
        {/* CABEÇALHO */}
        <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-3 border-b border-purple-100/60 shrink-0">
          <div>
            <h3 className="text-lg font-black text-[#24152F] font-serif">
              Registrar Peso
            </h3>
            <p className="text-xs text-gray-500 mt-0.5 font-medium">
              Por padrão, a data é hoje — você pode alterar se for retroativo.
            </p>
          </div>

          <button
            type="button"
            onClick={safeClose}
            disabled={saving}
            className="rounded-xl border border-gray-200 px-3 h-9 text-xs font-bold uppercase tracking-wider text-[#24152F] bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            Fechar
          </button>
        </div>

        {/* CONTEÚDO COM ROLAGEM */}
        <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
          {(error || success) && (
            <div
              className={`rounded-2xl border px-4 py-3 text-xs font-bold ${
                error
                  ? "bg-red-50 text-red-800 border-red-200"
                  : "bg-emerald-50 text-emerald-800 border-emerald-200"
              }`}
            >
              {error || success}
            </div>
          )}

          {/* CAMPOS PRINCIPAIS DE PESO (IGUAL À PÁGINA DE PESO) */}
          <div className="space-y-3.5">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">
                Peso (kg)
              </label>
              <input
                ref={firstInputRef}
                value={weightKg ?? ""}
                onChange={(e) => setWeightKg(sanitizeDecimalInput(e.target.value))}
                inputMode="decimal"
                type="text"
                autoComplete="off"
                placeholder="Ex: 58,5"
                className="input-base h-11 w-full text-sm font-bold box-border"
              />
            </div>

            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block mb-1">
                Data
              </label>
              <input
                value={entryDate || ""}
                onChange={(e) => setEntryDate(e.target.value)}
                type="date"
                className="input-base h-11 w-full text-sm font-bold box-border"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 block">
                  Observação (máx. 50 caracteres)
                </label>
                <span className="text-[10px] text-gray-400 font-semibold">
                  {(weightNotes || "").length}/50
                </span>
              </div>
              <input
                value={weightNotes ?? ""}
                maxLength={50}
                onChange={(e) => setWeightNotes(e.target.value.slice(0, 50))}
                type="text"
                placeholder="Ex: Estava menstruada e com retenção"
                className="input-base h-11 w-full text-sm box-border"
              />
            </div>
          </div>

          {/* OPÇÃO SECUNDÁRIA: MEDIDAS CORPORAIS */}
          <div className="rounded-2xl border border-purple-100 bg-purple-50/40 p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <span className="text-xs font-bold text-[#24152F] block">
                  Medidas corporais (opcional)
                </span>
                <span className="text-[11px] text-gray-500 block mt-0.5">
                  Abra para registrar detalhes em centímetros.
                </span>
              </div>

              <button
                type="button"
                className="rounded-xl border border-purple-200/80 px-3 h-9 text-xs font-bold uppercase tracking-wider text-[#6C54D8] bg-white hover:bg-purple-100/60 transition-colors cursor-pointer shrink-0"
                onClick={() => setShowBody((v) => !v)}
                disabled={saving}
              >
                {showBody ? "Ocultar" : "Inserir"}
              </button>
            </div>

            {showBody && (
              <div className="mt-3 pt-3 border-t border-purple-200/50 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field
                    name="left_arm_cm"
                    label="Braço esquerdo (cm)"
                    value={form.left_arm_cm}
                    onChange={(v) => setField("left_arm_cm", v)}
                  />
                  <Field
                    name="right_arm_cm"
                    label="Braço direito (cm)"
                    value={form.right_arm_cm}
                    onChange={(v) => setField("right_arm_cm", v)}
                  />
                  <Field
                    name="waist_cm"
                    label="Cintura (cm)"
                    value={form.waist_cm}
                    onChange={(v) => setField("waist_cm", v)}
                  />

                  {bodySexField === "chest" ? (
                    <Field
                      name="chest_cm"
                      label="Tórax (cm)"
                      value={form.chest_cm}
                      onChange={(v) => setField("chest_cm", v)}
                    />
                  ) : (
                    <Field
                      name="hip_cm"
                      label="Quadril (cm)"
                      value={form.hip_cm}
                      onChange={(v) => setField("hip_cm", v)}
                    />
                  )}

                  <Field
                    name="left_thigh_cm"
                    label="Coxa esquerda (cm)"
                    value={form.left_thigh_cm}
                    onChange={(v) => setField("left_thigh_cm", v)}
                  />
                  <Field
                    name="right_thigh_cm"
                    label="Coxa direita (cm)"
                    value={form.right_thigh_cm}
                    onChange={(v) => setField("right_thigh_cm", v)}
                  />
                  <Field
                    name="left_calf_cm"
                    label="Panturrilha esquerda (cm)"
                    value={form.left_calf_cm}
                    onChange={(v) => setField("left_calf_cm", v)}
                  />
                  <Field
                    name="right_calf_cm"
                    label="Panturrilha direita (cm)"
                    value={form.right_calf_cm}
                    onChange={(v) => setField("right_calf_cm", v)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RODAPÉ DO MODAL */}
        <div className="px-5 py-4 border-t border-purple-100/60 bg-gray-50/80 flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="flex-1 h-12 rounded-2xl bg-[#6C54D8] hover:bg-[#5A45BF] active:scale-95 text-white text-xs font-black uppercase tracking-wider shadow-md transition-all cursor-pointer disabled:opacity-60"
          >
            {saving ? "Salvando..." : "Salvar Peso"}
          </button>

          <button
            type="button"
            onClick={safeClose}
            disabled={saving}
            className="h-12 px-5 rounded-2xl bg-white hover:bg-gray-100 text-gray-700 text-xs font-bold uppercase tracking-wider border border-gray-200 transition-all cursor-pointer"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

export default MeasuresModal;